import { INSTALLMENT_OPTIONS, PAYMENT_PERIOD_LABELS } from './constants';

import type { PaymentOption, PaymentMethod, PaymentPeriodKey } from './types';

export const getCarrierSettings = (
  carrier: string,
  paymentConfig: PaymentOption[],
): PaymentOption | undefined => {
  const lowerCarrier = carrier.toLowerCase();

  const matches = paymentConfig.filter((option) =>
    option.carrier.some((carrierName) =>
      lowerCarrier.includes(carrierName.toLowerCase()),
    ),
  );

  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];

  return matches.reduce((best, current) => {
    const bestMatch = best.carrier.find((name) =>
      lowerCarrier.includes(name.toLowerCase()),
    );
    const currentMatch = current.carrier.find((name) =>
      lowerCarrier.includes(name.toLowerCase()),
    );

    return (currentMatch?.length || 0) > (bestMatch?.length || 0)
      ? current
      : best;
  });
};

export const getPaymentMethods = (
  settings: PaymentOption,
): Record<string, PaymentMethod> => {
  if (!settings) return {};

  const availablePaymentMethods: Record<string, PaymentMethod> = {};

  Object.entries(settings.payment_methods).forEach(([period, methods]) => {
    const hasAvailableMethod = Object.values(methods).some(
      (isAvailable) => isAvailable === true,
    );

    if (hasAvailableMethod) {
      availablePaymentMethods[period] = methods;
    }
  });

  return availablePaymentMethods;
};

export const getPaymentPeriodOptions = (
  availablePaymentMethods: Record<string, PaymentMethod>,
): Array<{ value: string; label: string }> => {
  return Object.entries(availablePaymentMethods).map(([period]) => ({
    value: period,
    label: PAYMENT_PERIOD_LABELS[period] || period,
  }));
};

export const getPaymentDateConfig = (
  carrierSettings: PaymentOption | undefined,
  paymentPeriod: string,
): PaymentOption['payment_dates']['other'] => {
  if (!carrierSettings) return null;
  const bucket =
    carrierSettings.payment_date_config_overrides?.[
      paymentPeriod as PaymentPeriodKey
    ] ?? (paymentPeriod === 'annual' ? 'annual' : 'other');
  return carrierSettings.payment_dates[bucket];
};

export const acceptsDownPaymentForPeriod = (
  carrierSettings: PaymentOption | undefined,
  paymentPeriod: string,
): boolean =>
  carrierSettings?.down_payment_accept_overrides?.[
    paymentPeriod as PaymentPeriodKey
  ] ??
  carrierSettings?.down_payment_accept ??
  false;

export const getPaymentPeriodNotice = (
  carrierSettings: PaymentOption | undefined,
  paymentPeriod: string,
): string | undefined =>
  carrierSettings?.payment_period_notices?.[paymentPeriod as PaymentPeriodKey];

export const isAgencyBillingAvailable = (
  carrierSettings: PaymentOption | undefined,
  paymentPeriod: string,
): boolean => {
  if (!carrierSettings || !paymentPeriod) return false;

  const periodMethods =
    carrierSettings.payment_methods[
      paymentPeriod as keyof typeof carrierSettings.payment_methods
    ];

  return periodMethods?.agency_bill === true;
};

export const calculateInstallments = (
  paymentPlan: string,
  carrierSettings?: PaymentOption,
): number => {
  if (paymentPlan === 'monthly') {
    return carrierSettings?.monthly_installments_count || 12;
  }
  return (
    INSTALLMENT_OPTIONS[paymentPlan as keyof typeof INSTALLMENT_OPTIONS] || 1
  );
};

export const calculateInstallmentAmount = (
  amountDue: number,
  numInstallments: number,
): string => {
  if (amountDue > 0 && numInstallments > 0) {
    return (amountDue / numInstallments).toFixed(2);
  }
  return '0.00';
};
